import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, cn, Divider, Link } from "@nextui-org/react";
import { faMemo } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Breakpoint, CenButton, useBreakpoint } from "cenero-ui-library";
import BookingStatusIndicator from "@/components/bookings/current/BookingStatusIndicator";
import { useAuthStore } from "@/store/AuthStore";
import { useBookingsCalendarStore } from "@/store/BookingsCalendarStore";
import { useBookingsCurrentStore } from "@/store/BookingsCurrentStore";
import { useErrorStore } from "@/store/ErrorStore";
import { ApiService } from "@/api/services/ApiService";
import { getBookingStatus, getDeviceType, getFormattedTime } from "@/lib/helpers";
import { BookingStatusLabel, IApiError, IBooking, ICheckinRequest } from "@/lib/types";

// ---------------------- Props

interface BookingCurrentProps {
  day: number;
  booking: IBooking;
}

// ---------------------- Component

const BookingCurrent = ({ day, booking }: BookingCurrentProps) => {
  // --------- Variables / Hooks

  const breakpoint = useBreakpoint();

  const isDesktop = breakpoint >= Breakpoint.xl;

  const navigate = useNavigate();

  const auth = useAuthStore();

  const {
    registerBooking,
    isBookingExpanded,
    setExpandedBooking,
    triggerRefetch: refetchCurrentBookings,
  } = useBookingsCurrentStore();

  const { triggerRefetch: refetchCalendarBookings } = useBookingsCalendarStore();

  const myId = booking.bookingId as string;

  const amIExpanded = isBookingExpanded(day, myId);

  const status = getBookingStatus(booking);

  const checkedIn = status === BookingStatusLabel.CheckedIn;

  const [isCheckingIn, setIsCheckingIn] = useState(false);

  const { setError } = useErrorStore();

  // --------- Effects

  useEffect(() => {
    registerBooking(day, myId);
  }, []);

  // Clear any global errors when component unmounts
  useEffect(() => {
    return () => {
      setError(null);
    };
  }, [setError]);

  // --------- Helpers

  const checkIn = async () => {
    try {
      setIsCheckingIn(true);

      let geolocation;

      try {
        geolocation = await ApiService.geolocation.getCurrentLocation();
      } catch (e) {
        const error = new Error("Failed to get location") as IApiError;

        error.problemDetails = {
          title:
            "Unable to get current location. Please ensure location services are enabled in your browser and that you are accessing the application via https and not http.",
        };
        setError(error);
      }

      if (geolocation) {
        const data: ICheckinRequest = {
          bookingId: myId,
          userId: auth.userSystemId ?? "",
          userName: auth.userName ?? "",
          latitude: geolocation.latitude,
          longitude: geolocation.longitude,
          address: geolocation.address?.display_name ?? "",
          deviceType: getDeviceType(geolocation.deviceType),
        };

        await ApiService.bookings.checkIn(data);

        refetchCurrentBookings();
        refetchCalendarBookings();
      }
    } catch (error) {
      setError(error as IApiError);
    } finally {
      setIsCheckingIn(false);
    }
  };

  // --------- JSX

  return (
    <>
      <div
        className={cn(
          "flex h-auto w-full flex-row items-center justify-start rounded-lg border border-gray-200 bg-white/20 py-2 transition-all duration-500 ease-in-out dark:border-gray-800 dark:bg-black/20",
          [
            !amIExpanded && "opacity-80",
            amIExpanded &&
              "my-1 border-gray-300 bg-gray-100/20 dark:border-gray-700 dark:bg-gray-800/10",
          ],
        )}
      >
        <div className="w-full">
          <div
            className="flex h-auto w-full flex-row items-center justify-between pt-1"
            onClick={() => setExpandedBooking(day, myId)}
          >
            {/*******************************************/}
            {/* Booking Information */}
            {/*******************************************/}

            <div className="flex h-auto flex-grow flex-col items-start justify-start px-4">
              <p className="w-full text-sm font-bold text-gray-800 dark:text-gray-200">
                {`${booking.city}, ${booking.state}`}
              </p>
              <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                {getFormattedTime(booking.localStartTime)}
              </p>

              {status && <BookingStatusIndicator status={status} />}
            </div>

            {/*******************************************/}
            {/* Show/Hide Button */}
            {/*******************************************/}

            <div className="mr-1 flex h-auto flex-row items-center justify-end gap-1">
              <Button
                isIconOnly
                radius="full"
                className="bg-transparent p-2 text-current transition-all duration-200"
                onPress={() => setExpandedBooking(day, myId)}
              >
                <FontAwesomeIcon
                  icon={amIExpanded ? faChevronUp : faChevronDown}
                  className="h-3 text-gray-500 transition-transform duration-500 xs:h-4"
                />
              </Button>
            </div>
          </div>

          <div
            className={cn(
              "overflow-hidden transition-all duration-500",
              amIExpanded ? "opacity-100" : "h-0 opacity-0",
            )}
          >
            <div className="mx-4 mb-2 mt-3">
              <Divider />
            </div>

            {/*******************************************/}
            {/* Booking Additional Information */}
            {/*******************************************/}

            <div className="flex h-auto w-full flex-col items-start justify-center">
              <div className="flex h-auto w-full flex-row items-start justify-between">
                <div className="ml-4 mr-1 flex flex-grow flex-col pt-[0.375rem]">
                  <p className="mb-1 w-full text-xs font-bold text-gray-800 dark:text-gray-200 sm:text-sm">
                    {booking.serviceAccountName}
                  </p>
                  <p className="font-base text-xs text-gray-500 dark:text-gray-300 sm:text-sm">
                    {booking.serviceRequestName}
                  </p>
                  <p className="font-base text-xs text-gray-500 dark:text-gray-300 sm:text-sm">
                    {booking.sowNumber}
                  </p>
                  <p className="font-base text-xs text-gray-500 dark:text-gray-300 sm:text-sm">
                    {booking.street}
                  </p>
                  <p className="font-base text-xs text-gray-500 dark:text-gray-300 sm:text-sm">
                    {`${booking.city}, ${booking.state} ${booking.postalCode}`}
                  </p>
                  <p className="font-base mt-1 text-xs font-bold text-gray-600 dark:text-gray-200 sm:text-sm">
                    {`${getFormattedTime(booking.localStartTime)} - ${getFormattedTime(booking.localEndTime)}`}
                  </p>
                </div>

                {/*******************************************/}
                {/* Show Details Button */}
                {/*******************************************/}

                <div className="mr-1 flex h-auto flex-row items-center justify-end">
                  <Button
                    isIconOnly
                    radius="full"
                    className="bg-transparent p-2 text-current transition-all duration-200"
                    as={Link}
                    href={`details/${myId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (isDesktop) {
                        navigate(`details/${myId}`, { replace: true });
                      } else {
                        navigate(`details/${myId}`);
                      }
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faMemo}
                      className="h-6 text-gray-400 transition-transform duration-500"
                    />
                  </Button>
                </div>
              </div>

              <div className="mb-2 mt-4 flex h-auto w-full flex-row items-start justify-start gap-2 px-4">
                {/*******************************************/}
                {/* Check In Button */}
                {/*******************************************/}

                <CenButton
                  isDisabled={checkedIn || isCheckingIn}
                  className={cn("h-12 w-1/2 max-w-56 text-sm sm:text-base", [
                    checkedIn && "opacity-30",
                    isCheckingIn && "opacity-50",
                  ])}
                  color="primary"
                  label="Check In"
                  onPress={checkIn}
                  variation="solid"
                  isLoading={isCheckingIn}
                />

                {/*******************************************/}
                {/* Check Out Button */}
                {/*******************************************/}

                <CenButton
                  isDisabled={!checkedIn}
                  className={cn("h-12 w-1/2 max-w-56 text-sm sm:text-base", [
                    !checkedIn && "opacity-30",
                  ])}
                  color="secondary"
                  label="Check Out"
                  variation="solid"
                  href={`checkout/${myId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (isDesktop) {
                      navigate(`checkout/${myId}`, { replace: true });
                    } else {
                      navigate(`checkout/${myId}`);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingCurrent;
